import './TechnologyPage.css';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from '@emotion/react';
import { Button } from '@mui/material';
import Header from '../../components/header/Header';
import KirHaplotypeTable from '../../components/KirHaplotypeTable/KirHaplotypeTable';
import {
  KIR_FAMILIES,
  KIR_HAPLOTYPES,
} from '../../database/kir-haplotypes';
import hla_map from '../../images/techpage/hla-table.png';
import typing_evolution from '../../images/techpage/hla-typing-evolution.png';
import phase_ambiguity from '../../images/techpage/hla-phase-ambiguity.png';
import kir_map from '../../images/techpage/kir-table.png';
import kir_publication from '../../files/publications/kir-haplotypes.pdf';

/**
 * Reference data. Allele counts come from the IPD-IMGT/HLA and IPD-KIR
 * statistics pages. To refresh, update ALLELE_SOURCE plus the counts below:
 *   https://www.ebi.ac.uk/ipd/imgt/hla/about/statistics/
 *   https://www.ebi.ac.uk/ipd/kir/about/statistics/
 */
const ALLELE_SOURCE = 'IPD-IMGT/HLA, June 2026 release';
const HLA_ALLELE_TOTAL = '45,421';
const KIR_ALLELE_TOTAL = '2,286';

const CLASS_I_ALLELES = [
  { gene: 'HLA-A', alleles: '9,279' },
  { gene: 'HLA-B', alleles: '11,258' },
  { gene: 'HLA-C', alleles: '9,416' },
  { gene: 'HLA-E', alleles: '390' },
  { gene: 'HLA-F', alleles: '139' },
  { gene: 'HLA-G', alleles: '204' },
];

const CLASS_II_ALLELES = [
  { gene: 'HLA-DRB1', alleles: '4,023' },
  { gene: 'HLA-DRB3/4/5', alleles: '1,397' },
  { gene: 'HLA-DQA1', alleles: '1,056' },
  { gene: 'HLA-DQB1', alleles: '3,121' },
  { gene: 'HLA-DPA1', alleles: '956' },
  { gene: 'HLA-DPB1', alleles: '3,170' },
];

const AT_A_GLANCE = [
  {
    figure: '11',
    label: 'loci typed in a single pass',
    detail: 'HLA-A, B, C, DRB1/3/4/5, DQA1, DQB1, DPA1 and DPB1.',
  },
  {
    figure: 'Phased',
    label: 'allele sequences, not allele lists',
    detail:
      'Sequencing individual PCR transcripts resolves heterozygotes without a separation step.',
  },
  {
    figure: '100s',
    label: 'of samples per sequencing run',
    detail:
      'Unique index tags let samples be pooled and reported individually.',
  },
];

const WORKFLOW_STEPS = [
  {
    step: '1',
    title: 'Amplify',
    description:
      'Assay-specific primers amplify the target loci from genomic DNA in a two-stage PCR.',
  },
  {
    step: '2',
    title: 'Index and pool',
    description:
      'Each sample receives a unique index pair, so scores to hundreds of samples share one run.',
  },
  {
    step: '3',
    title: 'Sequence',
    description:
      'Pooled libraries are clustered and sequenced by synthesis, producing millions of reads.',
  },
  {
    step: '4',
    title: 'Analyze',
    description:
      'ScisCloud™ assembles the reads into phased genotypes and reportable KIR haplotypes.',
  },
];

const NOMENCLATURE_FIELDS = [
  {
    token: 'HLA-',
    label: 'Prefix',
    detail: 'Identifies the gene as part of the HLA complex.',
  },
  {
    token: 'A',
    label: 'Gene',
    detail: 'The specific HLA locus — here, HLA-A.',
  },
  {
    token: '24',
    label: 'Allele group',
    detail:
      'First field. Corresponds to the serological antigen family the allele belongs to.',
  },
  {
    token: '02',
    label: 'Specific protein',
    detail:
      'Second field. A distinct protein sequence. Two fields is the usual reporting resolution.',
  },
  {
    token: '01',
    label: 'Synonymous change',
    detail:
      'Third field. A silent substitution within the coding region — the protein is unchanged.',
  },
  {
    token: '02',
    label: 'Non-coding change',
    detail:
      'Fourth field. A difference in an intron or an untranslated region.',
  },
  {
    token: 'L',
    label: 'Expression suffix',
    detail:
      'Optional. Flags altered expression — for example L for low, N for a null allele.',
  },
];

const TechnologyPage = ({ headers, theme }) => {
  return (
    <div>
      <Helmet>
        <title>Scisco Genetics Gene Sequencing Technology</title>
        <meta
          name='description'
          content='Scisco Genetics has developed a high resolution typing system based on Next Generation Sequencing (NGS) that results in several million reads that can be analyzed using ScisCloud™ to report unambiguous HLA allele types and KIR haplotypes for scores to hundreds of individual samples simultaneously.  Scisco has developed high accuracy typing kits that are simple to learn, and easy to use: HLA typing kits, KIR typing Kits, Chimerism typing kits'
        />
      </Helmet>
      <Header
        header={headers.filter((header) => header.key === 'technology')}
      />

      <div className='tech-main'>
        {/* ── HERO ── */}
        {/* Same shape as the Services, Kits and Machine Learning heroes: a
            centred block, a two-clause title with the second clause in
            orange, then short lead paragraphs. The "Our approach" eyebrow is
            gone — those pages do not carry one — but the later sections
            below keep theirs. */}
        <section className='tech-hero'>
          <div className='tech-hero-text'>
            <h1 className='tech-hero-title'>
              Fully Phased, High Resolution<br />
              <span className='tech-hero-accent'>Typing in a Single Pass</span>
            </h1>
            <p className='tech-hero-desc'>
              The HLA and KIR regions are the most variable stretches of the
              human genome, and that variation is exactly what makes them hard
              to&nbsp;type.
            </p>
            <p className='tech-hero-desc'>
              Scisco Genetics builds assays, sequencing workflows and analysis
              software around one goal: an unambiguous answer at every locus,
              on the first pass, at a cost comparable to the low and medium
              resolution methods it&nbsp;replaces.
            </p>
          </div>
        </section>

        {/* AT A GLANCE */}
        <section className='tech-band tech-band-white tech-band-tight'>
          <div className='tech-inner'>
            <div className='tech-content'>
              <div className='tech-glance'>
                {AT_A_GLANCE.map((item) => (
                  <div key={item.label} className='tech-glance-item'>
                    <p className='tech-glance-figure'>{item.figure}</p>
                    <p className='tech-glance-label'>{item.label}</p>
                    <p className='tech-glance-detail'>{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* NEXT GENERATION SEQUENCING */}
        <section className='tech-band tech-band-gray' id='ngs'>
          <div className='tech-inner'>
            <span className='tech-badge'>Sequencing</span>
            <h2 className='tech-heading'>Next Generation Sequencing (NGS)</h2>
            <p className='tech-desc'>
              High resolution HLA typing by conventional sequencing demands
              time-consuming and expensive strategies to separate ambiguous
              alleles and allele combinations. NGS approaches resolve allele
              phase directly, returning unambiguous pairwise combinations of
              alleles — and they do it at the cost of the low and
              medium-resolution methods laboratories already use, with no added
              complexity at the bench.
            </p>
            <p className='tech-desc'>
              Our system runs on the Illumina MiSeq, a sequencing-by-synthesis
              platform using fluorescently labeled reversible terminator
              nucleotides. After assay-specific amplification, samples are
              tagged with unique indexes, pooled, and applied to the instrument,
              where each library is amplified into an individual cluster and
              sequenced with universal primers. The result is several million
              reads per run, analyzed in{' '}
              <a href='/sciscloud' className='orange-links'>
                ScisCloud™
              </a>{' '}
              to report unambiguous HLA allele types and KIR haplotypes for
              scores to hundreds of samples at once.
            </p>
            <div className='tech-steps'>
              {WORKFLOW_STEPS.map((item) => (
                <div key={item.step} className='tech-step'>
                  <div className='tech-step-number'>{item.step}</div>
                  <p className='tech-step-title'>{item.title}</p>
                  <p className='tech-step-desc'>{item.description}</p>
                </div>
              ))}
            </div>
            <p className='tech-desc tech-aside'>
              The same chemistry is packaged for in-house use in the{' '}
              <a href='/kits' className='orange-links'>
                ScisGo™ kit line
              </a>
              , or run for you as a{' '}
              <a href='/services' className='orange-links'>
                typing service
              </a>
              .
            </p>
          </div>
        </section>

        {/* HLA COMPLEX */}
        <section className='tech-band tech-band-white' id='hla-complex'>
          <div className='tech-inner'>
            <span className='tech-badge'>Background</span>
            <h2 className='tech-heading'>The HLA complex</h2>
            <div className='tech-two-col'>
              <div className='tech-col'>
                <p className='tech-desc'>
                  The HLA complex — the human major histocompatibility complex
                  (MHC) — spans a 4 Mb region on chromosome 6p21.3 containing
                  more than 200 genes. Over 40% of them encode proteins with
                  immune functions, central to defense against infection and
                  cancer and to susceptibility and resistance in autoimmune
                  disease. The most polymorphic HLA genes act as
                  histocompatibility barriers, which makes them the critical
                  markers for donor-recipient matching in bone marrow
                  transplantation.
                </p>
                <p className='tech-desc'>
                  Disparity between donor and recipient HLA-A, -B, -C, -DR,
                  -DQA, -DQB, -DPA and -DPB alleles affects outcome in both
                  bone marrow and solid organ transplantation, and specific
                  alleles are associated with susceptibility and resistance to
                  autoimmune and infectious disease.
                </p>
              </div>
              <div className='tech-col'>
                <p className='tech-desc'>
                  The complex divides into three regions. Class I and class II
                  genes encode the molecules that bind peptide fragments and
                  present them to T lymphocytes. Class I molecules appear on
                  most cell types and present endogenous peptides — from cancer
                  cells, virally infected cells, or cells damaged in other ways
                  — to CD8+ T-cells, which kill the affected cell. Class II
                  molecules sit mainly on antigen presenting cells, sample the
                  extracellular environment, and present peptides such as
                  bacterial fragments to CD4+ T helper cells. Both classes are
                  fundamental to recognition of self and non-self. The class III
                  region carries cytokine genes and components of the complement
                  system.
                </p>
              </div>
            </div>

            <figure className='tech-figure'>
              <img
                src={hla_map}
                className='tech-figure-image'
                alt='Map of chromosome 6p21.31 showing HLA class I genes (F, G, A, E, C, B), class III genes (LTA, TNF, LTB, HSPA1L/1A/1B, C2, BF, C4A, C4B) and class II genes (DRA, DRB3, DRB2, DRB1, DQA1, DQB1, DOB, DMB, DMA, DOA, DPA1, DPB1, DPB2)'
              />
              <figcaption className='tech-figure-caption'>
                <strong>Figure 1.</strong> Gene organization of the HLA complex
                at chromosome 6p21.31, showing the class I, class III and class
                II regions.
              </figcaption>
            </figure>

            <h3 className='tech-subheading'>How much variation there is</h3>
            <p className='tech-desc'>
              The HLA complex is the most polymorphic region in the human
              genome, with {HLA_ALLELE_TOTAL} alleles characterized to date —
              and the list keeps growing as new sequences are submitted. For the
              current count, see the{' '}
              <a
                href='https://www.ebi.ac.uk/ipd/imgt/hla/'
                className='orange-links'
                target='_blank'
                rel='noreferrer'
              >
                IPD-IMGT/HLA database
              </a>
              .
            </p>
            <div className='tech-tables'>
              <div className='tech-table-wrap'>
                <p className='tech-table-title'>Class I</p>
                <table className='tech-table'>
                  <thead>
                    <tr>
                      <th scope='col'>Gene</th>
                      <th scope='col'>Alleles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLASS_I_ALLELES.map((row) => (
                      <tr key={row.gene}>
                        <th scope='row'>{row.gene}</th>
                        <td>{row.alleles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className='tech-table-wrap'>
                <p className='tech-table-title'>Class II</p>
                <table className='tech-table'>
                  <thead>
                    <tr>
                      <th scope='col'>Gene</th>
                      <th scope='col'>Alleles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLASS_II_ALLELES.map((row) => (
                      <tr key={row.gene}>
                        <th scope='row'>{row.gene}</th>
                        <td>{row.alleles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className='tech-source'>
              Allele counts from {ALLELE_SOURCE}. Scisco Genetics assays type
              eleven loci in a single pass: HLA-A, B, C, DRB1/3/4/5, DQA1,
              DQB1, DPA1 and DPB1.
            </p>
          </div>
        </section>

        {/* HLA NOMENCLATURE */}
        <section className='tech-band tech-band-gray' id='hla-nomenclature'>
          <div className='tech-inner'>
            <span className='tech-badge'>Reading a result</span>
            <h2 className='tech-heading'>HLA nomenclature</h2>
            <p className='tech-desc'>
              HLA allele names are assigned by the WHO Nomenclature Committee
              for Factors of the HLA System. Each colon-separated field adds
              resolution, so the number of fields in a name tells you how
              precisely the allele has been resolved.
            </p>

            <div className='nomenclature'>
              <div className='nomenclature-row' aria-hidden='true'>
                {NOMENCLATURE_FIELDS.map((field, index) => (
                  <div key={field.label} className='nomenclature-cell-group'>
                    {index === 2 && (
                      <span className='nomenclature-sep'>*</span>
                    )}
                    {index >= 3 && index <= 5 && (
                      <span className='nomenclature-sep'>:</span>
                    )}
                    <div className='nomenclature-cell'>{field.token}</div>
                  </div>
                ))}
              </div>
              <p className='nomenclature-full'>
                <span className='nomenclature-full-label'>Example</span>
                HLA-A*24:02:01:02L
              </p>
              <ol className='nomenclature-legend'>
                {NOMENCLATURE_FIELDS.map((field) => (
                  <li key={field.label} className='nomenclature-legend-item'>
                    <span className='nomenclature-legend-token'>
                      {field.token}
                    </span>
                    <span className='nomenclature-legend-label'>
                      {field.label}
                    </span>
                    <span className='nomenclature-legend-detail'>
                      {field.detail}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
            <p className='tech-source'>
              Full rules and the current allele list are maintained at{' '}
              <a
                href='https://hla.alleles.org/'
                className='orange-links'
                target='_blank'
                rel='noreferrer'
              >
                hla.alleles.org
              </a>
              .
            </p>
          </div>
        </section>

        {/* HLA TYPING */}
        <section className='tech-band tech-band-white' id='hla-typing'>
          <div className='tech-inner'>
            <div className='tech-content'>
              <span className='tech-badge'>Method</span>
              <h2 className='tech-heading'>HLA typing</h2>

              <h3 className='tech-subheading'>From serology to sequence</h3>
              <p className='tech-desc'>
                HLA typing began with serological and cellular methods that
                identified cell surface antigen specificities. The polymerase
                chain reaction moved typing to DNA, first through panels of
                sequence-specific primer pairs and probes that could only
                resolve the alleles known at the time. Fluorescent dye-labeled
                sequencing of PCR amplicons, run on automated genetic analyzers
                with dedicated analysis software, then became the gold standard
                for high resolution typing.
              </p>

              <figure className='tech-figure'>
                <img
                  src={typing_evolution}
                  className='tech-figure-image'
                  alt='Three stages of HLA typing before NGS. Serological and cellular typing read antibody reactivity at the cell surface and T lymphocyte responses to antigen-presenting cells, giving antigen specificities. PCR-based typing used sequence-specific amplification (PCR-SSP) and probe hybridization (PCR-SSOP) to match patterns to known alleles. Sanger sequence-based typing ran PCR amplicons on genetic analyzers with analysis software for high resolution typing.'
                />
                <figcaption className='tech-figure-caption'>
                  <strong>Figure 2.</strong> The evolution of HLA typing before
                  NGS. Serological and cellular typing, PCR-based typing and
                  Sanger sequence-based typing emerged over time and continued
                  in parallel.
                </figcaption>
              </figure>

              <h3 className='tech-subheading'>
                Why ambiguity became the problem
              </h3>
              <p className='tech-desc'>
                Every wave of DNA-based typing revealed more polymorphism, and
                the{' '}
                <a
                  href='https://www.ebi.ac.uk/ipd/imgt/hla/'
                  className='orange-links'
                  target='_blank'
                  rel='noreferrer'
                >
                  IPD-IMGT/HLA database
                </a>{' '}
                still grows with each release. Because polymorphic sequence
                cassettes are shared across many alleles, heterozygous samples
                frequently produce ambiguous results: the polymorphisms are
                visible, but their phase is not. To get a high resolution
                answer, laboratories had to add complex, slow and expensive
                steps to physically separate the two allele sequences.
              </p>
              <p className='tech-desc'>
                Next generation sequencing removes that step. Because individual
                PCR transcripts are sequenced, phase comes for free — producing
                fully phased high resolution HLA-A, B, C, DRB1/3/4/5, DQA1,
                DQB1, DPA1 and DPB1 typing in a single pass.
              </p>

              <figure className='tech-figure'>
                <img
                  src={phase_ambiguity}
                  className='tech-figure-image'
                  alt='Comparison of earlier DNA-based typing and NGS. Earlier typing sees mixed signals from two alleles at each site, A or G at site 1 and C or T at site 2, so two different pairings fit the same result: A with C and G with T, or A with T and G with C. With NGS, individual reads link the variants across both sites, assigning A and C to allele 1 and G and T to allele 2.'
                />
                <figcaption className='tech-figure-caption'>
                  <strong>Figure 3.</strong> Why phase matters. Earlier
                  DNA-based typing detects the variants at each site but not
                  which allele carries them, so more than one pairing fits the
                  same result. Because NGS sequences individual reads that span
                  the informative variants, each variant is assigned to its
                  allele.
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* KIR */}
        <section className='tech-band tech-band-gray' id='kir'>
          <div className='tech-inner'>
            <span className='tech-badge'>Background</span>
            <h2 className='tech-heading'>
              Killer cell Ig-like receptors (KIR)
            </h2>
            <div className='tech-two-col'>
              <div className='tech-col'>
                <p className='tech-desc'>
                  KIR genes encode receptors on the surface of natural killer
                  (NK) cells, part of the innate immune system's defense against
                  infected and malignant cells. The interaction between KIR
                  receptors and HLA class I molecules regulates NK cell
                  function, letting the cell distinguish normal cells from
                  infected or cancerous ones.
                </p>
                <p className='tech-desc'>
                  The KIR locus sits at chromosome 19q13.4 within the leukocyte
                  receptor complex (LRC) and varies in both gene content and
                  allelic polymorphism — {KIR_ALLELE_TOTAL} KIR alleles are
                  currently recognized in the{' '}
                  <a
                    href='https://www.ebi.ac.uk/ipd/kir/'
                    className='orange-links'
                    target='_blank'
                    rel='noreferrer'
                  >
                    IPD-KIR database
                  </a>
                  .
                </p>
              </div>
              <div className='tech-col'>
                <p className='tech-desc'>
                  Haplotypes fall into two major groups by gene content. Group A
                  is constant — five inhibitory genes and one activating gene.
                  Group B carries a variable number of inhibitory and activating
                  genes. Fifteen KIR genes and two pseudogenes have been
                  identified, split into centromeric and telomeric halves by the
                  framework genes KIR3DP1 and KIR2DL4. Because centromeric and
                  telomeric motifs recombine freely, the number of possible
                  haplotypes is large.
                </p>
                <p className='tech-desc'>
                  Our own work identified 7 major gene-content haplotypes built
                  from 4 centromeric and 2 telomeric motifs. Examining a further
                  9,024 chromosomes turned up 37 rare haplotypes differing from
                  those 7 by gene deletion, insertion or hybridization.
                </p>
              </div>
            </div>

            <figure className='tech-figure'>
              <img
                src={kir_map}
                className='tech-figure-image'
                alt='Map of the leukocyte receptor complex at chromosome 19q13.4 above a diagram of the seven major KIR gene-content haplotypes'
              />
              <figcaption className='tech-figure-caption'>
                <strong>Figure 4.</strong> The KIR region within the leukocyte
                receptor complex at 19q13.4, and the seven major gene-content
                haplotypes assembled from centromeric and telomeric motifs.
              </figcaption>
            </figure>

            <h3 className='tech-subheading'>Explore the haplotypes</h3>
            <p className='tech-desc'>
              Gene content for every haplotype we identified, searchable by name
              or variation note and filterable by family. Every position is
              aligned centromeric to telomeric, so a gene stays in its column
              across all {KIR_HAPLOTYPES.length} haplotypes, and selecting a
              gene gives its recorded copy count alongside the variation
              annotation for that haplotype. Chromosome counts and population
              frequencies are reported in{' '}
              <a
                href={kir_publication}
                className='orange-links'
                target='_blank'
                rel='noreferrer'
              >
                the publication (PDF)
              </a>
              .
            </p>

            <figure className='kir-figure'>
              <KirHaplotypeTable />
              <figcaption className='tech-figure-caption'>
                <strong>Figure 5.</strong> Gene content across{' '}
                {KIR_HAPLOTYPES.length} KIR haplotypes in{' '}
                {KIR_FAMILIES.length} families, by insertion, deletion and
                hybrid structure relative to the major haplotypes. Colors group
                the gene positions and do not encode insertion or deletion
                status.
              </figcaption>
            </figure>
          </div>
        </section>

        {/* CTA */}
        <section className='tech-cta'>
          <h2 className='tech-heading'>Bring this into your laboratory</h2>
          <p className='tech-desc tech-cta-desc'>
            Run the assays in house with ScisGo™ kits, send samples to our CLIA
            laboratory, or talk to us about which is the better fit for your
            work.
          </p>
          <div className='tech-cta-buttons'>
            <ThemeProvider theme={theme}>
              <Button
                href='/kits'
                color='primary'
                variant='contained'
                sx={{
                  borderRadius: '30px',
                  fontWeight: 700,
                  width: '220px',
                  height: '55px',
                }}
              >
                Explore kits
              </Button>
              <Button
                href='/contact'
                color='primary'
                variant='outlined'
                sx={{
                  borderRadius: '30px',
                  fontWeight: 700,
                  width: '220px',
                  height: '55px',
                  backgroundColor: 'white',
                }}
              >
                Talk to us
              </Button>
            </ThemeProvider>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TechnologyPage;
